<template>
  <div class="table-wrapper">
    <table>
      <thead>
        <tr ref="theadrow">
          <th v-if="showActiveToggle" class="" style="width: 80px">Active</th>
          <th
            v-for="column in columns"
            :key="column.field"
            @click="setSortColumn(column)"
          >
            <div class="col-header">
              {{ column.label }}
              <template v-if="!column.options || column.options.sortable">
                <fa
                  v-if="(column.field === sortColumn || column.sortField === sortColumn)"
                  class="arrow"
                  :class="{ 'fa-rotate-180': sortAscending }"
                  :icon="['fad', 'sort-down']"
                />
                <fa
                  v-else
                  class="arrow"
                  :icon="['fad', 'sort']"
                />
              </template>
            </div>
          </th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="paginated.length < 1">
          <td class="text-center" colspan="9001">Nothing to show here.</td>
        </tr>
        <tr v-for="row in paginated" :key="row._id || JSON.stringify(row)">
          <td v-if="showActiveToggle" >
            <toggle-button 
              v-model="row.active" 
              :color="{ checked: '#28a745', unchecked: '#b8b8b8' }"
              :sync="true"
              :width="30" 
              :height="20"
              @change="$store.dispatch(saveAction, row)"
            />
          </td>
          <td v-for="column in columns" :key="(row._id || JSON.stringify(row)) + column.field">
            <button
              v-if="column.options && typeof column.options.callback === 'function' && getRowField(row, column.field, column.options) !== '-'"
              class="table-link"
              @click="column.options.callback(row)"
            >
              <div v-html="stripTags(getRowField(row, column.field, column.options))"></div>
            </button>
            <div v-else v-html="stripTags(getRowField(row, column.field, column.options))"></div>
          </td>
          <td class="text-right">
            <div class="button-wrapper">
              <button
                v-for="(listener) in Object.keys(inlineListeners)"
                :key="listener"
                class="btn btn-outline-primary btn-sm mr-2"
                :class="`btn-outline-${listener === 'delete' ? 'danger' : 'primary'}`"
                @click="$emit(listener, row)"
              >
                {{ listener }}
              </button>
            </div>
          </td>
        </tr>
      </tbody>

      <tfoot>
        <tr>
          <td :colspan="colspan">
            <div class="pagination">
              <div class="pagination__item-wrapper">
                <div
                  v-for="(page, i) in pages"
                  :key="'page' + i"
                  class="pagination__item"
                  :class="{'pagination__item--active' : paginationIndex === i}"
                  @click="paginationIndex = i"
                >{{ (i + 1) }}</div>
              </div>

              <div class="pagination__page-size">
                <select name="" id="" v-model="paginationSize" @change="paginationIndex = 0">
                  <option value="50">50</option>
                  <option value="100">100</option>
                  <option value="200">200</option>
                </select>
              </div>
            </div>
          </td>
        </tr>
      </tfoot>
    </table>
  </div>
</template>
<script>
import { get } from 'lodash'
export default {
  props: ['columns', 'rows', 'show-active-toggle', 'save-action'],

  data () {
    return {
      colspan: 0,

      paginationIndex: 0,
      paginationSize: 50,

      sortAscending: true,
      sortColumn: this.columns[0].sortField ? this.columns[0].sortField : this.columns[0].field
    }
  },

  computed: {
    sortedRows () {
      return this.rows.sort((a, b) => {
        const firstVal = get(a, this.sortColumn)
        const secondVal = get(b, this.sortColumn)

        if (!this.sortAscending) {
          return Intl.Collator(undefined, { numeric: true, sensitivity: "base" }).compare(secondVal, firstVal)
        } else {
          return Intl.Collator(undefined, { numeric: true, sensitivity: "base" }).compare(firstVal, secondVal)
        }
      })
    },

    pages () {
      return Math.ceil(this.sortedRows.length / this.paginationSize)
    },

    paginated () {
      return this.sortedRows.slice(this.paginationIndex * this.paginationSize, (this.paginationIndex + 1) * this.paginationSize)
    },

    inlineListeners () {
      let listeners = this.$listeners
      delete listeners['save']

      return listeners
    }
  },

  watch: {
    pages () {
      this.paginationIndex = 0
    }
  },

  mounted () {
    const rowEl = this.$refs.theadrow
    if (rowEl) {
      this.colspan = rowEl.querySelectorAll('th').length
    } else {
      this.colspan = this.columns.length + 2
    }
  },

  methods: {
    getRowField (row, field, options = {}) {
      let fieldPathArray = field.split('.')
      let currentField = row
      
      fieldPathArray.forEach(field => {
        if (currentField && typeof currentField[field] !== 'undefined') {
          currentField = currentField[field]
        } else {
          currentField = 'Not Found'
        }
      })

      if (options.return) {
        return options.return(currentField, row)
      } else {
        return currentField
      }
    },

    setSortColumn (column) {
      if (column.options && !column.options.sortable) {
        return
      }

      const sortField = column.sortField ? column.sortField : column.field

      if (this.sortColumn === sortField) {
        this.sortAscending = !this.sortAscending
      } else {
        this.sortColumn = sortField
      }
    },

    stripTags: function (string) {
      if (string && typeof string === 'string') {
        let regex = /<\/?p[^>]*>/g
        return string.replace(regex, '')
      }

      return string
    }
  }
}
</script>
<style lang="scss" scoped>
.table-wrapper {
  border-radius: 6px;
  margin: 20px 0 0;
  overflow: scroll;

  @media screen and (min-height: 800px) {
    max-height: calc(100vh - 450px);
  }
}

table {
  border-collapse: separate;
  border-spacing: 0;
  // border: 1px solid #d4d4d4;
  // border-radius: 6px;
  // table-layout: fixed;
  width: 100%;

  thead {
    background-color: #e2e8f0;
    border-bottom: 1px solid #cbd5e0;
    font-size: 12px;
    position: sticky;
    text-transform: uppercase;
    top: 0;
    z-index: 10;
  }  

  th {
    cursor: pointer;
  }  

  tbody {
    background: #f4f5f7;
    overflow: auto;
  }

  tfoot {
    background: #fff;
    bottom: 0;
    position: sticky;
    width: 100%;
    z-index: 100;

    tr:hover {
      background-color: #fff;
    }
  }

  th, td {
    font-weight: 400;
    padding: 10px 15px;
    user-select: none;
  }

  tr {
    &:nth-child(2n) {
      background-color: #fbfbfb;
    }

    &:hover {
      background-color: rgba(#e2e8f0, 0.5);
    }
  }
}

.col-header {
  align-items: center;
  display: flex;
  position: relative;

  svg {
    margin-left: 6px;
    position: relative;
  }
}

.button-wrapper {
  display: flex;
  justify-content: flex-end;
}

.pagination {
  align-items: center;
  display: flex;
  position: relative;

  &__item-wrapper {
    display: flex;
    justify-content: center;
    width: 100%;
  }

  &__item {
    align-items: center;
    border: 1px solid #808080;
    border-radius: 3px;
    cursor: pointer;
    display: flex;
    justify-content: center;
    height: 21px;
    margin: 0 2px;
    text-align: center;
    width: 21px;

    &--active {
      background: #eaeaea;
    }
  }

  &__page-size {
    select {
      background: #eaeaea;
      border: 1px solid #808080;
      border-radius: 3px;
    }
  }
}
</style>
